import { Node, mergeAttributes } from '@tiptap/core';
import { syncIdAttributes } from '../../extensions/CustomUniqueId';
import { TextSelection } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import { canSplit } from '@tiptap/pm/transform';
import getZwspPlugin, { ZWSP } from '../../plugins/getZwspPlugin';
import getNestedInsertionGuardPlugin from '../../plugins/getNestedInsertionGuardPlugin';
import { TEMPLATE_FIELD_INLINE_NAME, templateFieldWrapperClass } from '../../../doc-engine/templateFields';
import placeCaretBelow from '../../utils/placeCaretBelow';
import { getFormFieldExitPos, isFieldInCard } from '../formField/formFieldHelpers';
import { selectFieldOnEdgeClick } from './selectFieldOnEdgeClick';

const TemplateFieldInlineNode = Node.create({
  name: TEMPLATE_FIELD_INLINE_NAME,

  group: 'inline inlineTemplateField',
  content: 'inline*',
  inline: true,
  isolating: true,
  atom: false,
  draggable: true,

  // `id` and `fieldId` come from the `addUniqueIdAttribute()` wrapper, not from this node.

  parseHTML() {
    return [{ tag: 'template-field-inline' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['template-field-inline', mergeAttributes(HTMLAttributes), 0];
  },

  addKeyboardShortcuts() {
    // Deletion handlers clamped to field boundaries. Dispatches explicit transactions
    // to bypass TipTap/browser inconsistencies with inline isolating nodes.
    const deleteBackward = (deleteWord: boolean): boolean => {
      const { state } = this.editor;
      const { $from, empty } = state.selection;

      for (let d = $from.depth; d >= 0; d--) {
        if ($from.node(d).type !== this.type) continue;

        if (!empty) return false; // let default deleteSelection handle range selections

        // Empty field (ZWSP-only): delete the whole node regardless of cursor position; a card's field stays.
        // In bound docs, TemplateLocking rejects this transaction - safe no-op.
        if ($from.node(d).textContent === ZWSP) {
          if (isFieldInCard(state.doc, $from.before(d))) return true;
          this.editor.view.dispatch(state.tr.delete($from.before(d), $from.after(d)));
          return true;
        }

        const fieldStart = $from.start(d);
        if ($from.pos <= fieldStart) return true; // at field start - swallow, nothing to delete

        if (deleteWord) {
          // Word-delete clamped to field start. ' ' separator treats inline nodes as word breaks.
          const text = state.doc.textBetween(fieldStart, $from.pos, ' ');
          const match = text.match(/\S+\s*$|\s+$/);
          const wordStart = match ? Math.max(fieldStart, $from.pos - match[0].length) : fieldStart;
          this.editor.view.dispatch(state.tr.delete(wordStart, $from.pos));
        } else {
          this.editor.view.dispatch(state.tr.delete($from.pos - 1, $from.pos));
        }
        return true;
      }
      return false; // cursor not in a field - use default
    };

    const deleteForward = (deleteWord: boolean): boolean => {
      const { state } = this.editor;
      const { $from, empty } = state.selection;

      for (let d = $from.depth; d >= 0; d--) {
        if ($from.node(d).type !== this.type) continue;

        if (!empty) return false; // let default deleteSelection handle range selections

        // Empty field (ZWSP-only): delete the whole node regardless of cursor position; a card's field stays.
        if ($from.node(d).textContent === ZWSP) {
          if (isFieldInCard(state.doc, $from.before(d))) return true;
          this.editor.view.dispatch(state.tr.delete($from.before(d), $from.after(d)));
          return true;
        }

        const fieldEnd = $from.end(d);
        if ($from.pos >= fieldEnd) return true; // at field end - swallow, nothing to delete

        if (deleteWord) {
          const text = state.doc.textBetween($from.pos, fieldEnd, ' ');
          const match = text.match(/^\s*\S+|^\s+/);
          const wordEnd = match ? Math.min(fieldEnd, $from.pos + match[0].length) : fieldEnd;
          this.editor.view.dispatch(state.tr.delete($from.pos, wordEnd));
        } else {
          this.editor.view.dispatch(state.tr.delete($from.pos, $from.pos + 1));
        }
        return true;
      }
      return false;
    };

    // Enter at field boundary: split parent textblock outside the isolating node.
    // Mid-field Enter is swallowed - inline fields can't be split.
    const enterInField = (): boolean => {
      const { state } = this.editor;
      const { $from, empty } = state.selection;
      if (!empty) return false;

      for (let d = $from.depth; d >= 0; d--) {
        if ($from.node(d).type !== this.type) continue;

        const atStart = $from.pos <= $from.start(d);
        const atEnd = $from.pos >= $from.end(d);
        if (!atStart && !atEnd) return true; // mid-field: swallow

        // A card holds one line, which cannot split: at the end of its field, Enter leaves the card.
        const exitPos = atEnd ? getFormFieldExitPos($from) : undefined;
        if (exitPos !== undefined) {
          this.editor.view.dispatch(placeCaretBelow(state.tr, exitPos));
          return true;
        }

        // Split at the field boundary (outside the isolating node) so that
        // content after the split point moves to the new textblock.
        const splitPos = atEnd ? $from.after(d) : $from.before(d);
        if (!canSplit(state.doc, splitPos)) return true;
        const tr = state.tr.split(splitPos);
        const mapped = tr.mapping.map(splitPos, 1);
        tr.setSelection(TextSelection.near(tr.doc.resolve(mapped), 1));
        this.editor.view.dispatch(tr.scrollIntoView());
        return true;
      }
      return false;
    };

    return {
      Backspace: () => deleteBackward(false),
      'Alt-Backspace': () => deleteBackward(true),
      Delete: () => deleteForward(false),
      'Alt-Delete': () => deleteForward(true),
      Enter: () => enterInField(),
    };
  },

  addProseMirrorPlugins() {
    // Field-in-field is the block field's guard; this one keeps block content out of an inline field.
    const isBlockInInlineField = (node: PMNode, ancestors: readonly PMNode[]) =>
      node.isBlock && ancestors.some((ancestor) => ancestor.type === this.type);
    return [
      getZwspPlugin(this.type, { pluginKey: 'templateFieldInlineZwsp' }),
      getNestedInsertionGuardPlugin(isBlockInInlineField, 'templateFieldInlineGuard'),
    ];
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const wrapper = document.createElement('span');
      wrapper.className = templateFieldWrapperClass(TEMPLATE_FIELD_INLINE_NAME);
      syncIdAttributes(wrapper, node);
      wrapper.setAttribute('data-node-view-wrapper', '');

      const content = document.createElement('span');
      content.className = 'template-field-inline-content';
      content.setAttribute('data-node-view-content', '');

      wrapper.append(content);
      const stopSelectingOnEdgeClick = selectFieldOnEdgeClick(wrapper, editor, getPos);

      return {
        dom: wrapper,
        contentDOM: content,
        update(updatedNode) {
          if (updatedNode.type !== node.type) return false;
          syncIdAttributes(wrapper, updatedNode);
          return true;
        },
        destroy: stopSelectingOnEdgeClick,
      };
    };
  },
});

export default TemplateFieldInlineNode;
